import { useT } from "@transifex/react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import SidneyCard from "../../../basic/SidneyCard";
import { FormattedText } from "../../../complex/teacherArea/FormattedText";
import { useClassDisplayInfo } from "../../../hooks/useClassDisplayInfo";

interface WrongClassMemberNoticeProps {
    memberName: string;
    schoolClassId: string;
    onSwitchAccount: () => void;
}

/**
 * Shown when a logged-in member opens a shared result that isn't theirs — most often because they
 * are logged into a different teacher's classroom for the same school class. Names the session they
 * are in so they can tell which one they need to switch to.
 */
const WrongClassMemberNotice = (props: WrongClassMemberNoticeProps) => {
    const t = useT();

    // Stays hidden until settled rather than showing the name-only fallback and swapping a beat later.
    const session = useClassDisplayInfo(props.schoolClassId);

    const t_wrongMember = t("This result belongs to a different class or a different student.", {
        _tags: "StudentResult, WrongClassMemberNotice",
    });
    const t_switchAccount = t("Switch account", { _tags: "StudentResult, WrongClassMemberNotice" });

    // A literal per variant: Transifex extracts source strings statically, and translators need a
    // grammatically complete sentence rather than one carrying empty placeholders. The <b> tags are
    // rendered by FormattedText, which sanitizes down to b/i/br — so the interpolated names, which
    // the teacher types, can't smuggle markup in.
    const getIdentityLine = (): string => {
        if (!session) return "";
        if (session.className && session.teacherDisplayName) {
            return t(
                "You are logged in as <b>{memberName}</b> in class <b>{schoolClassName}</b> with <b>{teacherName}</b>.",
                {
                    _tags: "StudentResult, WrongClassMemberNotice",
                    memberName: props.memberName,
                    schoolClassName: session.className,
                    teacherName: session.teacherDisplayName,
                }
            );
        }
        if (session.className) {
            return t("You are logged in as <b>{memberName}</b> in class <b>{schoolClassName}</b>.", {
                _tags: "StudentResult, WrongClassMemberNotice",
                memberName: props.memberName,
                schoolClassName: session.className,
            });
        }
        return t("You are logged in as <b>{memberName}</b>.", {
            _tags: "StudentResult, WrongClassMemberNotice",
            memberName: props.memberName,
        });
    };

    const identityLine = getIdentityLine();

    return (
        <SidneyCard dataTestId="student-result-access-denied">
            <div className="flex flex-col items-center gap-l w-full">
                <div className="flex flex-col items-center gap-xs">
                    <p
                        role="alert"
                        className="text-l text-p6-grey-dark"
                    >
                        {t_wrongMember}
                    </p>
                    {identityLine && (
                        <FormattedText
                            rawText={identityLine}
                            textColor="text-l text-p6-grey-dark [&_b]:font-semibold"
                        />
                    )}
                </div>
                <button
                    type="button"
                    onClick={props.onSwitchAccount}
                    className="flex items-center gap-xxs bg-transparent border-none cursor-pointer p-no"
                    data-testid="student-result-switch-account"
                >
                    <span className="text-l text-p6-grey-dark">{t_switchAccount}</span>
                    <PhaseSixIcon
                        name="chevron-right"
                        className="text-[24px] text-p6-grey-dark"
                    />
                </button>
            </div>
        </SidneyCard>
    );
};

export default WrongClassMemberNotice;
